import React from 'react'
import { Link } from 'react-router-dom'
import { ToolkitPageLayout } from '../../../components'
import {
  AnnualReviewNurseImage,
  JobDescriptionNurseImage,
  AnnualReviewClinicalTechnicianImage,
  JobDescriptionClinicalTechnicianImage,
  AnnualReviewAppImage,
  ReviewGuideAppImage
} from 'assets'

interface Breadcrumb {
  label: string
  path: string
}

export const ClinicsStaffReviewsPage: React.FC = () => {
  const pageTitle = 'Staff Review and Job Descriptions'
  const breadcrumbs: Breadcrumb[] = [
    { label: 'Toolkit', path: '/toolkit' },
    { label: 'Clinics', path: '/toolkit/clinics' },
    { label: pageTitle, path: '/toolkit/clinics/staff-reviews' }
  ]

  const frontDeskJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2022/07/Front-Desk-Job-Description.pdf'
  const nurseAnnualReviewUrl =
    'https://docs.google.com/document/d/e/2PACX-1vTSCbhETAPuEWiXj8_HerHfwcvFDLW_-FygtZq22km2AOc1-Kb2aAvv5pht4KytqmOqEBKLU_Zcp9_p/pub'
  const nurseJobDescriptionUrl =
    'https://docs.google.com/document/d/e/2PACX-1vRqjXNqHv4QmtdYP1RuI_P9zxYGW4veIAvvTrCUtYGEV5ytQNxE7vKTvFP4cpPwZQoMvBcTFzi6J1O7/pub'
  const clinicalTechAnnualReviewUrl =
    'https://docs.google.com/document/d/e/2PACX-1vSyidXbwiWqoEoV5RLgcbee8LeJhu5UPnB0mFZmyqgEBo9ugS8aB4NOxgFPBeDk8zKoYHEDDma--z4H/pub'
  const clinicalTechJobDescriptionUrl =
    'https://docs.google.com/document/d/e/2PACX-1vTAz1A-6Abcx345mDcaRtWnqzhswB9Nd3dFF_0aGr1khl15vP7A_rVSYwvBnA16PIhkBy8KM-Uj9sRe/pub'
  const socialWorkerJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2022/07/Social-Worker-Job-Description.pdf'
  const chwPatientNavigatorJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2023/05/CHW-Patient-Navigator.pdf'
  const lcswJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2023/05/Licensed-Clinical-Social-Worker.pdf'
  const caseManagerPositionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2023/05/Case-Manager-Position-1.pdf'
  const transitionCoordinatorJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2023/05/Transition-Coordiantor-Job-Description.pdf'
  const highRiskCoordinatorJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2024/02/Job-Description-HIgh-RIsk-Coordinator.docx'
  const appAnnualReviewUrl =
    'https://docs.google.com/document/d/e/2PACX-1vTJjMN31UQ-1LsWg3iGecaZqDG5cMQfkEQnIgAYWJqT53Fem_4jW70mqwJF6vzpRdDPZ0AsRAV7aRns/pub'
  const appReviewGuideUrl =
    'https://docs.google.com/document/d/e/2PACX-1vTVZiF2xz4fM6LEuarw9_6r6E8gsZTVRp61kLbqH1A1IKnudLdiTue-GtvZWDyeNUG-Tjxofw1kQ1KY/pub'
  const appJobDescriptionUrl =
    'https://scdtoolkit.jh.edu/wp-content/uploads/2022/07/Job-Description-APP.pdf'

  const commonLinkClasses =
    'inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] transition-colors duration-200'
  const downloadIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 mr-2"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
        clipRule="evenodd"
      />
    </svg>
  )

  return (
    <ToolkitPageLayout title={pageTitle} breadcrumbs={breadcrumbs}>
      <div className="prose max-w-none text-[#3C2416]">
        <p className="text-lg italic text-center mb-8">
          The goal is to provide tools to support building your clinic&apos;s
          team and holding team members accountable for career growth.
        </p>
        <p className="mb-12 text-center">
          Infusion Clinic position descriptions and annual reviews for:
        </p>

        {/* IC Front Desk Staff */}
        <hr className="my-8" />
        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          IC Front Desk Staff
        </h2>
        <div className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white">
          <h3 className="text-xl font-semibold text-[#142841] mb-3">
            Front Desk Job Description
          </h3>
          <a
            href={frontDeskJobDescriptionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={commonLinkClasses}
          >
            {downloadIcon}
            Download PDF
          </a>
        </div>

        {/* IC Nurse */}
        <hr className="my-8" />
        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          IC Nurse
        </h2>
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <figure className="p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
            <a
              href={nurseAnnualReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={AnnualReviewNurseImage}
                alt="Annual Review of Nurse document screenshot"
                className="max-w-full h-auto rounded-md shadow-sm inline-block"
              />
            </a>
            <figcaption className="mt-2 text-sm text-gray-700">
              <em>Annual Review of Nurse</em>
            </figcaption>
          </figure>
          <figure className="p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
            <a
              href={nurseJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={JobDescriptionNurseImage}
                alt="Job Description of Nurse document screenshot"
                className="max-w-full h-auto rounded-md shadow-sm inline-block"
              />
            </a>
            <figcaption className="mt-2 text-sm text-gray-700">
              <em>Job Description of Nurse</em>
            </figcaption>
          </figure>
        </div>

        {/* IC Clinical Technician */}
        <hr className="my-8" />
        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          IC Clinical Technician
        </h2>
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <figure className="p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
            <a
              href={clinicalTechAnnualReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={AnnualReviewClinicalTechnicianImage}
                alt="Annual Review of Clinical Technician document screenshot"
                className="max-w-full h-auto rounded-md shadow-sm inline-block"
              />
            </a>
            <figcaption className="mt-2 text-sm text-gray-700">
              <em>Annual Review of Clinical Technician</em>
            </figcaption>
          </figure>
          <figure className="p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
            <a
              href={clinicalTechJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={JobDescriptionClinicalTechnicianImage}
                alt="Job Description of Clinical Technician document screenshot"
                className="max-w-full h-auto rounded-md shadow-sm inline-block"
              />
            </a>
            <figcaption className="mt-2 text-sm text-gray-700">
              <em>Job Description of Clinical Technician</em>
            </figcaption>
          </figure>
        </div>

        {/* IC Social Worker, Community Health Worker, and Patient Navigator */}
        <hr className="my-8" />
        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          IC Social Worker, Community Health Worker, and Patient Navigator
        </h2>
        <div className="space-y-6 mb-8">
          <div className="p-4 border border-gray-200 rounded-lg shadow-md bg-white">
            <h3 className="text-xl font-semibold text-[#142841] mb-3">
              Social Worker Job Description
            </h3>
            <a
              href={socialWorkerJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={commonLinkClasses}
            >
              {downloadIcon}
              Download PDF
            </a>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg shadow-md bg-white">
            <h3 className="text-xl font-semibold text-[#142841] mb-3">
              CHW Patient Navigator Job Description
            </h3>
            <a
              href={chwPatientNavigatorJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={commonLinkClasses}
            >
              {downloadIcon}
              Download PDF
            </a>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg shadow-md bg-white">
            <h3 className="text-xl font-semibold text-[#142841] mb-3">
              Licensed Clinical Social Worker (LCSW) Job Description
            </h3>
            <a
              href={lcswJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={commonLinkClasses}
            >
              {downloadIcon}
              Download PDF
            </a>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg shadow-md bg-white">
            <h3 className="text-xl font-semibold text-[#142841] mb-3">
              Case Manager Position Description
            </h3>
            <a
              href={caseManagerPositionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={commonLinkClasses}
            >
              {downloadIcon}
              Download PDF
            </a>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg shadow-md bg-white">
            <h3 className="text-xl font-semibold text-[#142841] mb-3">
              Transition Coordinator Job Description
            </h3>
            <a
              href={transitionCoordinatorJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={commonLinkClasses}
            >
              {downloadIcon}
              Download PDF
            </a>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg shadow-md bg-white">
            <h3 className="text-xl font-semibold text-[#142841] mb-3">
              High Risk Coordinator Job Description
            </h3>
            <a
              href={highRiskCoordinatorJobDescriptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={commonLinkClasses}
            >
              {downloadIcon}
              Download DOCX
            </a>
          </div>
        </div>

        {/* IC Advanced Practice Practitioner */}
        <hr className="my-8" />
        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          IC Advanced Practice Practitioner
        </h2>
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <figure className="p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
            <a
              href={appAnnualReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={AnnualReviewAppImage}
                alt="Annual Review of Advanced Practice Practitioner document screenshot"
                className="max-w-full h-auto rounded-md shadow-sm inline-block"
              />
            </a>
            <figcaption className="mt-2 text-sm text-gray-700">
              <em>Annual Review of Advanced Practice Practitioner</em>
            </figcaption>
          </figure>
          <figure className="p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
            <a
              href={appReviewGuideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={ReviewGuideAppImage}
                alt="Review Guide for Advanced Practice Practitioner document screenshot"
                className="max-w-full h-auto rounded-md shadow-sm inline-block"
              />
            </a>
            <figcaption className="mt-2 text-sm text-gray-700">
              <em>Review Guide for Advanced Practice Practitioner</em>
            </figcaption>
          </figure>
        </div>
        <div className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white">
          <h3 className="text-xl font-semibold text-[#142841] mb-3">
            Job Description Advanced Practice Provider
          </h3>
          <a
            href={appJobDescriptionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={commonLinkClasses}
          >
            {downloadIcon}
            Download PDF
          </a>
        </div>

        <hr className="my-12" />

        {/* All Done Button */}
        <div className="mt-12 mb-8 text-center">
          <Link
            to="/toolkit/clinics" // Link to the main clinics page
            className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-6 py-3 text-lg font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            All done? Click here to return to the Clinics screen
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
